import React, { Component } from 'react'
import { AiOutlineReload, AiOutlineCheckCircle, AiOutlineWarning, AiOutlineCloseCircle } from "react-icons/ai"
import { getInformationWithDataV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import "../../css/general-style.css"
import '../../css/dashboard.css'
import { Menu } from "../../components/Menu/Menu"

// Same-page-scoped inline styles as ProductPricing.js — btn-outline-* classes
// render unstyled/invisible in this app's global CSS until hover (hit twice
// already this project, see feedback memory), so buttons here are solid with
// explicit colors instead of relying on Bootstrap outline variants.
const BTN_PRIMARY = { background: '#2563EB', border: 'none', borderRadius: '8px', color: '#FFFFFF', fontWeight: 700, padding: '0.5rem 1.1rem', fontSize: '0.85rem', cursor: 'pointer' }
const BTN_DANGER = { background: '#DC2626', border: 'none', borderRadius: '8px', color: '#FFFFFF', fontWeight: 700, padding: '0.5rem 1.1rem', fontSize: '0.85rem', cursor: 'pointer' }
const BTN_SECONDARY = { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', color: '#0F172A', fontWeight: 600, padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer' }
const BTN_DISABLED = { ...BTN_PRIMARY, background: '#94A3B8', cursor: 'not-allowed' }

const STATUS_POLL_INTERVAL_MS = 5000
const STATUS_POLL_MAX_TRIES = 8 // ~40s, matches how long a real send took manually (PM69910)

const money = (n) => `$${Number(n || 0).toFixed(2)}`

// No optional chaining / nullish coalescing in this project's CRA version
// (react-scripts 2.1.3 — crashes the compiler, see feedback memory).
const errorMessageOf = (response) => {
    if (response && response.message) return response.message
    if (response && response.error) return response.error
    return ''
}

/**
 * Tools → SO Recovery Tool (2026-09-25).
 *
 * Automates the manual recipe used live for PM69901 → PM69910: two
 * different customers ended up saved under the same SalesOrderNo in
 * create_csv, Sage only kept one, and the other had to be rebuilt under a
 * fresh number and resent by hand. Admin-only (backend re-checks
 * userCompany.idTypeofUser server-side on every call — this page's own
 * TypeUser check is just so a non-admin doesn't see a broken page).
 *
 * Mirrors the two verification points from the manual process:
 *   1. Preview — finds + live-verifies a free folio before anything is sent.
 *   2. Confirm — re-verifies the folio is still free right before sending,
 *      then polls Sage afterwards for a real completeness check (line
 *      totals match the original payload, not just "the header exists").
 *
 * Mayaland only for now (idCompany=3) — see soRecoveryTool.ts header for why
 * Altitude's dual-leg case is a deferred phase 2.
 */
export class SORecoveryTool extends Component {
    constructor(props) {
        super(props)
        this.state = {
            isAdmin: getValueCookie('TypeUser') === '1',
            scanning: false,
            scanError: '',
            collisions: [],
            previews: {},       // createCsvId -> preview data
            previewLoading: {}, // createCsvId -> bool
            previewError: {},   // createCsvId -> string
            confirmLoading: {}, // createCsvId -> bool
            recoveryLogs: {},   // createCsvId -> { logId, status, liveStatus, ... }
            dismissLoading: {}, // createCsvId -> bool
        }
        this.pollTimers = {}
    }

    componentDidMount() {
        this.scan()
    }

    componentWillUnmount() {
        Object.values(this.pollTimers).forEach((t) => clearInterval(t))
    }

    scan = async () => {
        this.setState({ scanning: true, scanError: '' })
        const response = await getInformationWithDataV3('/tools/so-recovery/scan', {})
        if (response && response.status && response.status.code === 1) {
            this.setState({ collisions: response.data || [], scanning: false })
        } else {
            this.setState({ scanning: false, scanError: errorMessageOf(response) || 'Could not scan for collisions.' })
        }
    }

    preview = async (createCsvId) => {
        this.setState((s) => ({ previewLoading: { ...s.previewLoading, [createCsvId]: true }, previewError: { ...s.previewError, [createCsvId]: '' } }))
        const response = await getInformationWithDataV3('/tools/so-recovery/preview', { createCsvId })
        if (response && response.status && response.status.code === 1) {
            this.setState((s) => ({
                previews: { ...s.previews, [createCsvId]: response.data },
                previewLoading: { ...s.previewLoading, [createCsvId]: false },
            }))
        } else {
            this.setState((s) => ({
                previewLoading: { ...s.previewLoading, [createCsvId]: false },
                previewError: { ...s.previewError, [createCsvId]: errorMessageOf(response) || 'Preview failed.' },
            }))
        }
    }

    cancelPreview = (createCsvId) => {
        this.setState((s) => {
            const previews = { ...s.previews }
            delete previews[createCsvId]
            return { previews }
        })
    }

    confirm = async (createCsvId, proposedNoOrder) => {
        this.setState((s) => ({ confirmLoading: { ...s.confirmLoading, [createCsvId]: true } }))
        const response = await getInformationWithDataV3('/tools/so-recovery/confirm', { createCsvId, proposedNoOrder })
        if (response && response.status && response.status.code === 1) {
            const { logId, newNoOrder } = response.data
            this.setState((s) => ({
                confirmLoading: { ...s.confirmLoading, [createCsvId]: false },
                recoveryLogs: { ...s.recoveryLogs, [createCsvId]: { logId, newNoOrder, status: 'sent', liveStatus: 'checking', tries: 0 } },
            }))
            this.startPolling(createCsvId, logId)
        } else {
            this.setState((s) => ({
                confirmLoading: { ...s.confirmLoading, [createCsvId]: false },
                recoveryLogs: { ...s.recoveryLogs, [createCsvId]: { status: 'failed', liveStatus: 'error', errorMessage: errorMessageOf(response) || 'Send failed.' } },
            }))
        }
    }

    startPolling = (createCsvId, logId) => {
        const poll = async () => {
            const current = this.state.recoveryLogs[createCsvId]
            const tries = (current && current.tries ? current.tries : 0) + 1
            const response = await getInformationWithDataV3('/tools/so-recovery/status', { logId })
            if (response && response.status && response.status.code === 1) {
                const data = response.data
                const liveStatus = data.liveStatus || (data.status === 'verified' ? 'verified' : 'checking')
                this.setState((s) => ({ recoveryLogs: { ...s.recoveryLogs, [createCsvId]: { ...data, logId, tries, liveStatus } } }))
                if (data.status === 'verified' || data.status === 'failed' || tries >= STATUS_POLL_MAX_TRIES) {
                    clearInterval(this.pollTimers[createCsvId])
                    delete this.pollTimers[createCsvId]
                    if (data.status === 'verified') this.scan() // refresh list so this entry drops off
                }
            } else {
                clearInterval(this.pollTimers[createCsvId])
                delete this.pollTimers[createCsvId]
            }
        }
        this.pollTimers[createCsvId] = setInterval(poll, STATUS_POLL_INTERVAL_MS)
        poll()
    }

    dismiss = async (createCsvId) => {
        const reason = window.prompt('Optional note for this dismissal (not a real collision, already fixed manually, etc.):', '') || ''
        this.setState((s) => ({ dismissLoading: { ...s.dismissLoading, [createCsvId]: true } }))
        const response = await getInformationWithDataV3('/tools/so-recovery/dismiss', { createCsvId, reason })
        this.setState((s) => ({ dismissLoading: { ...s.dismissLoading, [createCsvId]: false } }))
        if (response && response.status && response.status.code === 1) {
            this.scan()
        } else {
            window.alert(errorMessageOf(response) || 'Could not dismiss this entry.')
        }
    }

    renderEntry(noOrder, entry) {
        const { previews, previewLoading, previewError, confirmLoading, recoveryLogs, dismissLoading } = this.state
        const preview = previews[entry.createCsvId]
        const log = recoveryLogs[entry.createCsvId]

        if (entry.isLive) {
            return (
                <div key={entry.createCsvId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.8rem', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', marginBottom: '0.5rem' }}>
                    <AiOutlineCheckCircle style={{ color: '#16A34A', fontSize: '1.1rem', flexShrink: 0 }} />
                    <div style={{ fontSize: '0.82rem' }}>
                        <strong>{entry.customerName}</strong> ({entry.customerNo}) — {money(entry.totalCost)}, {entry.lineCount} lines — currently live in Sage under {noOrder}
                    </div>
                </div>
            )
        }

        return (
            <div key={entry.createCsvId} style={{ padding: '0.6rem 0.8rem', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <AiOutlineWarning style={{ color: '#DC2626', fontSize: '1.1rem', flexShrink: 0 }} />
                    <div style={{ fontSize: '0.82rem', flex: 1 }}>
                        <strong>{entry.customerName}</strong> ({entry.customerNo}) — {money(entry.totalCost)}, {entry.lineCount} lines — saved by {entry.userName} on {new Date(entry.createdAt).toLocaleString()} — lost, never landed in Sage
                    </div>
                    {!log && !preview && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button style={previewLoading[entry.createCsvId] ? BTN_DISABLED : BTN_PRIMARY} disabled={previewLoading[entry.createCsvId]} onClick={() => this.preview(entry.createCsvId)}>
                                {previewLoading[entry.createCsvId] ? 'Checking...' : 'Recover'}
                            </button>
                            <button style={dismissLoading[entry.createCsvId] ? BTN_DISABLED : BTN_SECONDARY} disabled={dismissLoading[entry.createCsvId]} onClick={() => this.dismiss(entry.createCsvId)}>
                                {dismissLoading[entry.createCsvId] ? 'Dismissing...' : 'Dismiss'}
                            </button>
                        </div>
                    )}
                </div>

                {previewError[entry.createCsvId] && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#DC2626' }}>{previewError[entry.createCsvId]}</div>
                )}

                {preview && !log && (
                    <div style={{ marginTop: '0.6rem', padding: '0.6rem 0.8rem', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                        <div style={{ fontSize: '0.82rem', marginBottom: '0.5rem' }}>
                            Proposed new order number: <strong>{preview.proposedNoOrder}</strong> — verified free in Sage just now.
                            <br />Will resend {preview.customerName} ({preview.customerNo}), {money(preview.totalCost)}, {preview.lineCount} lines, {preview.totalQty} units.
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button style={confirmLoading[entry.createCsvId] ? BTN_DISABLED : BTN_PRIMARY} disabled={confirmLoading[entry.createCsvId]} onClick={() => this.confirm(entry.createCsvId, preview.proposedNoOrder)}>
                                {confirmLoading[entry.createCsvId] ? 'Sending...' : 'Confirm and send'}
                            </button>
                            <button style={BTN_SECONDARY} onClick={() => this.cancelPreview(entry.createCsvId)}>Cancel</button>
                        </div>
                    </div>
                )}

                {log && this.renderRecoveryStatus(log)}
            </div>
        )
    }

    renderRecoveryStatus(log) {
        if (log.liveStatus === 'error' || log.status === 'failed') {
            return (
                <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#DC2626', fontSize: '0.82rem' }}>
                    <AiOutlineCloseCircle /> Failed to send: {log.errorMessage || 'unknown error'}
                </div>
            )
        }
        if (log.status === 'verified') {
            const lineCount = log.liveLineCount !== undefined && log.liveLineCount !== null ? log.liveLineCount : log.lineCount
            const total = log.liveTotal !== undefined && log.liveTotal !== null ? log.liveTotal : log.totalCost
            return (
                <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#16A34A', fontSize: '0.82rem' }}>
                    <AiOutlineCheckCircle /> Verified in Sage as {log.newNoOrder} — {lineCount} lines, {money(total)}, matches the original order.
                </div>
            )
        }
        if (log.liveStatus === 'completeness_mismatch') {
            return (
                <div style={{ marginTop: '0.6rem', color: '#B45309', fontSize: '0.82rem' }}>
                    <AiOutlineWarning /> {log.newNoOrder} exists in Sage but totals don't match yet — expected {money(log.expectedTotal)}, Sage shows {money(log.liveTotal)} ({log.liveLineCount} lines). Still checking — this may just be mid-import; if it doesn't resolve, needs manual review.
                </div>
            )
        }
        return (
            <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748B', fontSize: '0.82rem' }}>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Sent as {log.newNoOrder} — verifying in Sage (this can take up to a minute)...
            </div>
        )
    }

    render() {
        const { isAdmin, scanning, scanError, collisions } = this.state

        if (!isAdmin) {
            return (
                <div className="dash-page" style={{ paddingTop: '70px' }}>
                    <Menu />
                    <div style={{ padding: '2rem' }}>This tool is only available to administrators.</div>
                </div>
            )
        }

        return (
            <div className="dash-page" style={{ paddingTop: '70px' }}>
                <Menu />

                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2563EB 100%)', padding: '2rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                        <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.5rem', margin: 0 }}>SO Recovery Tool</h1>
                        <p style={{ color: 'rgba(255,255,255,0.65)', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                            Mayaland only — finds sales orders lost to folio collisions and rebuilds them under a fresh, verified-free number.
                        </p>
                    </div>
                    <button style={scanning ? BTN_DISABLED : BTN_SECONDARY} disabled={scanning} onClick={this.scan}>
                        <AiOutlineReload style={{ marginRight: '0.4rem' }} />
                        {scanning ? 'Scanning...' : 'Rescan'}
                    </button>
                </div>

                <div style={{ padding: '1.5rem 2rem' }}>
                    {scanning && collisions.length === 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748B', fontSize: '0.9rem' }}>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            Scanning the last 45 days for folio collisions...
                        </div>
                    )}

                    {scanError && (
                        <div style={{ color: '#DC2626', fontSize: '0.85rem', marginBottom: '1rem' }}>{scanError}</div>
                    )}

                    {!scanning && !scanError && collisions.length === 0 && (
                        <div style={{ color: '#16A34A', fontSize: '0.9rem' }}>
                            <AiOutlineCheckCircle style={{ marginRight: '0.4rem' }} /> No unresolved folio collisions found.
                        </div>
                    )}

                    {collisions.map((c) => (
                        <div key={c.noOrder} className="card mb-3">
                            <div className="card-body">
                                <h6 style={{ fontWeight: 700, marginBottom: '0.75rem' }}>Order No: {c.noOrder}</h6>
                                {c.entries.map((entry) => this.renderEntry(c.noOrder, entry))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }
}

export default SORecoveryTool
